import { useState } from 'react';

import { useModalFocus } from '../lib/useModalFocus.js';

import type { SelectableScene } from './AddRequirementDialog.js';

/**
 * ONE REQUIREMENT THAT IS REALLY TWO PLACES.
 *
 * The mirror of a merge, and the more expensive of the two mistakes: two hotel
 * rooms the model read as one cost a production a location it never scouted,
 * never budgeted and never booked. Splitting says which scenes belong to the
 * second place; the rest stay where they are.
 *
 * NOT THE ADD DIALOG WITH A DIFFERENT TITLE. Adding says a location exists that
 * the reading missed, and flags the result human-confirmed. A split says the
 * model DID read this place — it read it as part of another one — so the new
 * requirement stays a model proposal and the grouping is what was wrong. The
 * two screens also constrain differently: a split must leave at least one scene
 * behind, or it is a rename with extra steps.
 *
 * SCOPED TO THE SCENES THIS REQUIREMENT ALREADY COVERS. Offering the whole
 * screenplay would invite moving a scene that was never on it, which the server
 * refuses — a form that can compose a request the server will not accept is a
 * form that teaches producers not to trust it.
 */
export function SplitRequirementDialog({
  requirementTitle,
  scenes,
  busy = false,
  error = null,
  onCancel,
  onSubmit,
}: {
  requirementTitle: string;
  /** Only the scenes currently on the requirement being split. */
  scenes: readonly SelectableScene[];
  busy?: boolean;
  error?: string | null;
  onCancel: () => void;
  onSubmit: (input: { title: string; sceneIds: readonly string[] }) => void;
}) {
  const [title, setTitle] = useState('');
  const [selected, setSelected] = useState<readonly string[]>([]);
  const modal = useModalFocus<HTMLDivElement>(() => {
    if (!busy) onCancel();
  });

  const toggle = (sceneId: string) => {
    setSelected((current) =>
      current.includes(sceneId) ? current.filter((id) => id !== sceneId) : [...current, sceneId],
    );
  };

  const trimmed = title.trim();
  const staying = scenes.length - selected.length;
  // The server's two refusals, enforced here so they are never met: a
  // requirement needs a name, and a split has to leave something behind.
  const submittable = trimmed !== '' && selected.length > 0 && staying > 0 && !busy;

  return (
    <div
      data-testid="split-requirement-dialog"
      onKeyDown={modal.onKeyDown}
      role="dialog"
      aria-modal="true"
      aria-label={`Split ${requirementTitle}`}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(35, 35, 35, 0.45)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '5vh 16px',
        overflowY: 'auto',
        zIndex: 40,
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) onCancel();
      }}
    >
      <div
        ref={modal.ref}
        style={{
          background: 'var(--color-bg)',
          width: 'min(640px, 100%)',
          border: '1px solid var(--color-divider)',
          boxShadow: '0 18px 48px rgba(16, 24, 40, 0.18)',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 12,
            flexWrap: 'wrap',
            padding: '18px 22px',
            borderBottom: '2px solid var(--ink)',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontFamily: 'var(--font-heading)',
              fontSize: 22,
              letterSpacing: '0.02em',
            }}
          >
            SPLIT REQUIREMENT
          </h2>
          <span className="mm" style={{ fontSize: 13 }} data-testid="split-requirement-subject">
            {`“${requirementTitle}” is more than one place`}
          </span>
          <button
            type="button"
            className="ctl"
            data-testid="split-requirement-close"
            aria-label="Cancel"
            disabled={busy}
            onClick={onCancel}
            style={{ marginLeft: 'auto' }}
          >
            ×
          </button>
        </header>

        <div style={{ padding: '18px 22px' }}>
          <label
            className="flbl"
            htmlFor="split-requirement-name"
            style={{ display: 'block', marginBottom: 6 }}
          >
            Name of the second location
          </label>
          <input
            id="split-requirement-name"
            data-testid="split-requirement-name"
            value={title}
            disabled={busy}
            placeholder="e.g. Hotel room — Marie's"
            maxLength={255}
            onChange={(event) => setTitle(event.target.value)}
            style={{
              width: '100%',
              padding: '11px 12px',
              border: '2px solid var(--ink)',
              font: 'inherit',
              background: 'var(--color-bg)',
            }}
          />

          <p className="flbl" style={{ margin: '18px 0 8px' }}>
            Which scenes move to it?
          </p>
          <div
            data-testid="split-requirement-scenes"
            style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
          >
            {scenes.map((option) => {
              const on = selected.includes(option.id);
              return (
                <button
                  key={option.id}
                  type="button"
                  className="ctl"
                  data-testid={`split-requirement-scene-${option.id}`}
                  data-on={on ? '1' : '0'}
                  aria-pressed={on}
                  disabled={busy}
                  onClick={() => toggle(option.id)}
                  style={{ display: 'inline-flex', gap: 6, alignItems: 'baseline' }}
                >
                  <span style={{ fontWeight: 700 }}>Sc {option.sceneNumber ?? '—'}</span>
                  <span className="mm" style={{ fontSize: 12 }}>
                    {option.slugline ?? 'Untitled'}
                  </span>
                </button>
              );
            })}
          </div>

          {/* The outcome in the producer's own terms, recomputed as they pick:
              a split is not undoable from this screen, so what it will do has
              to be readable before it is pressed, not after. */}
          <p
            data-testid="split-requirement-outcome"
            style={{
              marginTop: 18,
              padding: '12px 14px',
              background: 'var(--color-surface)',
              border: '1px solid var(--color-divider)',
              fontSize: 13,
              lineHeight: 1.6,
            }}
          >
            {selected.length === 0 ? (
              <span className="mm">Pick the scenes that belong to the second location.</span>
            ) : staying === 0 ? (
              <span className="mm">
                That is every scene. Moving them all is a rename, not a split — leave at least one
                behind.
              </span>
            ) : (
              <>
                <strong>{`${String(selected.length)} ${selected.length === 1 ? 'scene moves' : 'scenes move'} to the new requirement.`}</strong>{' '}
                <span className="mm">
                  {`${String(staying)} ${staying === 1 ? 'stays' : 'stay'} on “${requirementTitle}”. Both remain model proposals — the reading found this place, it grouped it wrongly.`}
                </span>
              </>
            )}
          </p>

          {error === null ? null : (
            <p
              data-testid="split-requirement-error"
              style={{ marginTop: 12, fontSize: 13, color: 'var(--danger)' }}
            >
              {error}
            </p>
          )}
        </div>

        <footer
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            padding: '14px 22px',
            borderTop: '1px solid var(--color-divider)',
            background: 'var(--color-surface)',
          }}
        >
          <span className="mm" style={{ fontSize: 12 }}>
            Values and constraints stay with the original — set the new one&apos;s on its card.
          </span>
          <button
            type="button"
            className="btn btn-ghost"
            data-testid="split-requirement-cancel"
            disabled={busy}
            onClick={onCancel}
            style={{ marginLeft: 'auto' }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            data-testid="split-requirement-submit"
            disabled={!submittable}
            onClick={() => onSubmit({ title: trimmed, sceneIds: selected })}
          >
            {busy ? 'Splitting…' : 'Split'}
          </button>
        </footer>
      </div>
    </div>
  );
}
